import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ModalContext = createContext(null);

/**
 * Custom modal provider that replaces native browser alert/confirm/prompt dialogs.
 * 
 * Usage:
 *   const { showAlert, showConfirm, showPrompt } = useModal();
 *   await showAlert('Something happened');
 *   const ok = await showConfirm('Are you sure?');
 *   const value = await showPrompt('Enter name:', 'default');
 */
export function ModalProvider({ children }) {
  const [modal, setModal] = useState(null);
  const resolveRef = useRef(null);
  const inputRef = useRef(null);

  // Focus input when prompt modal opens
  useEffect(() => {
    if (modal?.type === 'prompt' && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [modal]);

  const closeModal = useCallback((result) => {
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
    setModal(null);
  }, []);

  const showAlert = useCallback((message, title = 'Notice') => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModal({ type: 'alert', title, message });
    });
  }, []);

  const showConfirm = useCallback((message, options = {}) => {
    const { title = 'Confirm', okText = 'OK', cancelText = 'Cancel', danger = false } = options;
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModal({ type: 'confirm', title, message, okText, cancelText, danger });
    });
  }, []);

  const showPrompt = useCallback((message, defaultValue = '', options = {}) => {
    const { title = 'Input', okText = 'OK', cancelText = 'Cancel', placeholder = '' } = options;
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModal({ type: 'prompt', title, message, defaultValue, okText, cancelText, placeholder });
    });
  }, []);

  // Handle keyboard events
  const handleKeyDown = useCallback((e) => {
    if (!modal) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (modal.type === 'alert') closeModal(undefined);
      else if (modal.type === 'confirm') closeModal(null);
      else if (modal.type === 'prompt') closeModal(null);
    }
    if (e.key === 'Enter' && modal.type !== 'prompt') {
      e.stopPropagation();
      if (modal.type === 'alert') closeModal(undefined);
      else if (modal.type === 'confirm') closeModal(true);
    }
  }, [modal, closeModal]);

  const handlePromptSubmit = useCallback((e) => {
    e.preventDefault();
    const value = inputRef.current?.value ?? '';
    closeModal(value);
  }, [closeModal]);

  return (
    <ModalContext.Provider value={{ showAlert, showConfirm, showPrompt }}>
      {children}
      {modal && (
        <div
          className="custom-modal-overlay"
          onKeyDown={handleKeyDown}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              if (modal.type === 'alert') closeModal(undefined);
              else if (modal.type === 'confirm') closeModal(null);
              else if (modal.type === 'prompt') closeModal(null);
            }
          }}
        >
          <div className="custom-modal" role="dialog" aria-modal="true">
            <div className="custom-modal-header">
              <h3>{modal.title}</h3>
              <button
                className="custom-modal-close"
                onClick={() => {
                  if (modal.type === 'alert') closeModal(undefined);
                  else if (modal.type === 'confirm') closeModal(null);
                  else if (modal.type === 'prompt') closeModal(null);
                }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="custom-modal-body">
              <p>{modal.message}</p>
              {modal.type === 'prompt' && (
                <form onSubmit={handlePromptSubmit}>
                  <input
                    ref={inputRef}
                    type="text"
                    className="custom-modal-input"
                    defaultValue={modal.defaultValue}
                    placeholder={modal.placeholder}
                    autoFocus
                  />
                </form>
              )}
            </div>
            <div className="custom-modal-footer">
              {modal.type === 'alert' && (
                <button
                  className="btn btn-primary"
                  onClick={() => closeModal(undefined)}
                  autoFocus
                >
                  OK
                </button>
              )}
              {modal.type === 'confirm' && (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => closeModal(false)}
                  >
                    {modal.cancelText || 'Cancel'}
                  </button>
                  <button
                    className={`btn ${modal.danger ? 'btn-danger' : 'btn-primary'}`}
                    onClick={() => closeModal(true)}
                    autoFocus
                  >
                    {modal.okText || 'OK'}
                  </button>
                </>
              )}
              {modal.type === 'prompt' && (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => closeModal(null)}
                  >
                    {modal.cancelText || 'Cancel'}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => closeModal(inputRef.current?.value ?? '')}
                  >
                    {modal.okText || 'OK'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
